import type { Template, Visibility } from '@/config/objects/templates';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  FormPageLayout,
  Input,
  RichTextEditor,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  SwitchField,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface TemplateFormData {
  name: string;
  description: string;
  estimatedDuration: string;
  coverImageUrl: string;
  visibility: Visibility;
  isFeatured: boolean;
  tags: string;
}

const DEFAULT_FORM: TemplateFormData = {
  name: '',
  description: '',
  estimatedDuration: '',
  coverImageUrl: '',
  visibility: 'private',
  isFeatured: false,
  tags: '',
};

export function TemplateFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const [formData, setFormData] = useState<TemplateFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);
  const firstErrorRef = useRef<HTMLDivElement>(null);

  const updateField = useCallback(
    <K extends keyof TemplateFormData>(key: K, value: TemplateFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // Load existing template (edit mode)
  const {
    data: templateResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: Template }>({
    queryKey: ['Template', itemId],
    queryFn: () => apiClient.get(`/templates/${itemId}`),
    enabled: !!itemId,
  });

  useEffect(() => {
    if (!templateResponse?.data) return;
    const t = templateResponse.data;
    setFormData({
      name: t.name ?? '',
      description: t.description ?? '',
      estimatedDuration: t.estimatedDuration ?? '',
      coverImageUrl: t.coverImageUrl ?? '',
      visibility: t.visibility ?? 'private',
      isFeatured: t.isFeatured ?? false,
      tags: t.tags?.join(', ') ?? '',
    });
    setLastSaved(new Date(t.updatedAt));
    setFormReady(true);
  }, [templateResponse]);

  useEffect(() => {
    if (isError) {
      toast.error('Template not found');
      navigate('/templates');
    }
  }, [isError, navigate]);

  // Validation
  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};

    if (!formData.name.trim()) errs.name = 'Name is required';

    if (formData.coverImageUrl && !/^(https?:\/\/)?[\w.-]+\.\w{2,}/.test(formData.coverImageUrl)) {
      errs.coverImageUrl = 'Please enter a valid URL';
    }

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(`field-${firstKey}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  };

  // Prepare payload
  const preparePayload = () => ({
    name: formData.name.trim(),
    description: formData.description.trim() || null,
    estimatedDuration: formData.estimatedDuration.trim() || null,
    coverImageUrl: formData.coverImageUrl.trim() || null,
    visibility: formData.visibility,
    isFeatured: formData.isFeatured,
    tags: formData.tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0),
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return apiClient.put<{ data: Template }>(`/templates/${itemId}`, payload);
      }
      return apiClient.post<{ data: Template }>('/templates', payload);
    },
    onSuccess: (response: { data: Template }) => {
      queryClient.invalidateQueries({ queryKey: ['Template'] });
      setLastSaved(new Date());
      if (isEdit) {
        toast.success('Template saved successfully');
      } else {
        toast.success('Template created successfully');
        navigate(`/templates/${response.data.id}`);
      }
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save template');
    },
  });

  const handleSave = () => {
    if (!validateForm()) return;
    saveMutation.mutate(preparePayload());
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(`/templates/${itemId}`);
    } else {
      navigate('/templates');
    }
  };

  // Loading skeleton
  if (isEdit && (isLoading || !formReady)) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2, 3].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 !pb-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {isEdit ? 'Edit Template' : 'Create Template'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit ? formData.name || 'Update template details' : 'Create a new game template'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} loading={saveMutation.isPending} icon={Save}>
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <div ref={firstErrorRef} className="space-y-6">
          {/* Template Details */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Template Details</CardTitle>
              <CardDescription>Basic information about the template</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup name="name" label="Name" required error={errors.name}>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Enter template name"
                  className={cn(errors.name && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup name="description" label="Description">
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => updateField('description', value ?? '')}
                />
              </FormFieldGroup>

              <FormFieldGroup
                name="estimatedDuration"
                label="Estimated Duration"
                description="e.g., 2 hours, 45 minutes"
              >
                <Input
                  id="estimatedDuration"
                  value={formData.estimatedDuration}
                  onChange={(e) => updateField('estimatedDuration', e.target.value)}
                  placeholder="Enter estimated duration"
                />
              </FormFieldGroup>

              <FormFieldGroup
                name="coverImageUrl"
                label="Cover Image URL"
                error={errors.coverImageUrl}
              >
                <Input
                  id="coverImageUrl"
                  value={formData.coverImageUrl}
                  onChange={(e) => updateField('coverImageUrl', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className={cn(errors.coverImageUrl && 'border-destructive')}
                />
              </FormFieldGroup>
            </CardContent>
          </Card>

          {/* Visibility */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Visibility</CardTitle>
              <CardDescription>Control who can see this template</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <FormFieldGroup name="visibility" label="Visibility">
                  <Select
                    value={formData.visibility}
                    onValueChange={(v) => updateField('visibility', v as Visibility)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Private</SelectItem>
                      <SelectItem value="unlisted">Unlisted</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                </FormFieldGroup>
              </div>

              <SwitchField
                label="Featured"
                description="Highlight this template in the marketplace"
                checked={formData.isFeatured}
                onCheckedChange={(checked) => updateField('isFeatured', checked)}
              />
            </CardContent>
          </Card>

          {/* Tags */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Tags</CardTitle>
              <CardDescription>Categorize this template</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <FormFieldGroup name="tags" label="Tags" description="Separate multiple tags with commas">
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => updateField('tags', e.target.value)}
                  placeholder="e.g., corporate, team-building, outdoor"
                />
              </FormFieldGroup>
            </CardContent>
          </Card>
        </div>
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} loading={saveMutation.isPending} icon={Save}>
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
